import { get } from 'lodash';

import { mediaUtils, isMatchedById } from '../helpers';
import {
    CustomBlockKind,
    FeaturesBlockKind,
    HatBlockKind,
    ImageBlockKind,
    ImageGalleryBlockKind,
    ServicesGridBlockKind,
} from './blocks';

export const getBlockIdsRecursively = (page, block) =>
    recursivelyVisitNestedBlocks(page, block, (item) => item._id).results;

export const recursivelyVisitNestedBlocks = (page, block, consumer) => {
    const results = [];
    let processed = 0;

    switch (block.kind) {
        case CustomBlockKind: {
            for (const { blockId } of block.areas) {
                const orphan = page.blocks.find(isMatchedById(blockId));

                if (orphan) {
                    const { processed: p, results: r } = recursivelyVisitNestedBlocks(page, orphan, consumer);

                    processed += p;
                    results.push(...r);
                }
            }

            break;
        }
    }

    const result = consumer(block);

    if (result !== false) {
        processed++;

        if (result) {
            results.push(result);
        }
    }

    return {
        processed,
        results,
    };
};

export const UploadUsage = {
    Image: 'Image',
    Background: 'Background',
    Logo: 'Logo',
    Icon: 'Icon',
};

const makeUploadMatcher = ({ path }) => {
    const relative = mediaUtils.getRelativeUrl(path);

    return (other) => other && (path === other || relative === mediaUtils.getRelativeUrl(other));
};

const getImageUsageInBlock = (block, matchPath) => {
    switch (block.kind) {
        case ImageBlockKind: {
            return matchPath(block.url) && [{ entity: ImageBlockKind, as: UploadUsage.Image }];
        }
        case ImageGalleryBlockKind: {
            return block.images
                .map(({ url }) => matchPath(url) && { entity: ImageGalleryBlockKind, as: UploadUsage.Image })
                .filter(Boolean);
        }
        case HatBlockKind: {
            return [
                matchPath(block.bgUrl) && { entity: HatBlockKind, as: UploadUsage.Background },
                matchPath(block.logoUrl) && { entity: HatBlockKind, as: UploadUsage.Logo },
            ].filter(Boolean);
        }
        case FeaturesBlockKind: {
            return block.items
                .map(
                    (item) =>
                        item.glyph.type === 'image' &&
                        matchPath(item.glyph.url) && { entity: FeaturesBlockKind, as: UploadUsage.Icon }
                )
                .filter(Boolean);
        }
        case ServicesGridBlockKind: {
            return []
                .concat(
                    ...block.items.map((item) =>
                        item.photos.map(
                            (url) => matchPath(url) && { entity: ServicesGridBlockKind, as: UploadUsage.Image }
                        )
                    )
                )
                .filter(Boolean);
        }
    }
};

export const getUploadUsagesOnPage = (page, matchPath) => {
    const usages = [];

    if (matchPath(get(page, 'seo.ogImageUrl'))) {
        usages.push({ entity: 'page.og-image', as: UploadUsage.Image });
    }

    if (matchPath(get(page, 'theme.page.backgroundImage'))) {
        usages.push({ entity: 'page', as: UploadUsage.Background });
    }

    for (const block of page.blocks) {
        const found = getImageUsageInBlock(block, matchPath);

        if (found && found.length) {
            usages.push(...found);
        }
    }

    return usages;
};

export const getUploadUsagesOnWebsite = (upload, { pages, seo }) => {
    const matchPath = makeUploadMatcher(upload);
    const usages = [];

    if (matchPath(seo.ogImageUrl)) {
        usages.push({ entity: 'website.og-image', as: UploadUsage.Image });
    }

    for (const page of pages) {
        const found = getUploadUsagesOnPage(page, matchPath);

        if (found.length) {
            usages.push(...found);
        }
    }

    return usages;
};

export const isImageUsedOnPage = (page, matchPath) => {
    if (matchPath(get(page, 'seo.ogImageUrl'))) {
        return true;
    }

    if (matchPath(get(page, 'theme.page.backgroundImage'))) {
        return true;
    }

    return !!page.blocks.find((block) => {
        switch (block.kind) {
            case ImageBlockKind: {
                return matchPath(block.url);
            }
            case ImageGalleryBlockKind: {
                return !!block.images.find(({ url }) => matchPath(url));
            }
            case HatBlockKind: {
                return matchPath(block.bgUrl) || matchPath(block.logoUrl);
            }
            case FeaturesBlockKind: {
                return !!block.items.find((item) => item.glyph.type === 'image' && matchPath(item.glyph.url));
            }
            case ServicesGridBlockKind: {
                return !!block.items.find((item) => item.photos.find(matchPath));
            }
        }
    });
};

export const isUploadUsedOnPage = (upload, page) => {
    return isImageUsedOnPage(page, makeUploadMatcher(upload));
};

export const isUploadUsedOnWebsite = (upload, { pages, seo }) => {
    const matchPath = makeUploadMatcher(upload);

    if (matchPath(seo.ogImageUrl)) {
        return true;
    }

    for (const page of pages) {
        if (isImageUsedOnPage(page, matchPath)) {
            return true;
        }
    }
};
